import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ChevronRight, ChevronDown, FolderOpen, FolderClosed, File, Search, 
  Lock, Plus, GripVertical, X, BookOpen, Share2, Heart, Bookmark,
  MessageSquare, Star
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { useProgress } from '@/hooks/useProgress';
import { useAuth } from '@/hooks/useAuth';
import { Progress } from './ui/progress';
import { Tooltip } from './ui/tooltip';

interface ContentItem {
  id: string;
  title: string;
  type: 'folder' | 'file';
  slug?: string;
  isPremium?: boolean;
  children?: ContentItem[];
}

interface ContentSidebarProps {
  onSectionSelect: (section: any) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

// Initial content structure
const initialStructure: ContentItem[] = [
  {
    id: 'dsa',
    title: 'Data Structures & Algorithms',
    type: 'folder',
    children: [
      {
        id: 'arrays',
        title: 'Arrays',
        type: 'folder',
        children: [
          {
            id: 'arrays-introduction',
            title: 'Introduction to Arrays',
            type: 'file',
            slug: 'arrays-introduction'
          },
          {
            id: 'array-operations',
            title: 'Array Operations',
            type: 'file',
            slug: 'array-operations'
          },
          {
            id: 'array-algorithms',
            title: 'Array Algorithms',
            type: 'file',
            slug: 'array-algorithms',
            isPremium: true
          }
        ]
      },
      {
        id: 'linked-lists',
        title: 'Linked Lists',
        type: 'folder',
        children: [
          {
            id: 'linked-lists-basics',
            title: 'Linked Lists Basics',
            type: 'file',
            slug: 'linked-lists-basics'
          },
          {
            id: 'linked-lists-operations',
            title: 'Linked List Operations',
            type: 'file',
            slug: 'linked-lists-operations',
            isPremium: true
          }
        ]
      }
    ]
  },
  {
    id: 'system-design',
    title: 'System Design',
    type: 'folder',
    children: [
      {
        id: 'basics',
        title: 'Basics',
        type: 'file',
        slug: 'system-design-basics'
      },
      {
        id: 'scalability',
        title: 'Scalability',
        type: 'file',
        slug: 'system-design-scalability',
        isPremium: true
      }
    ]
  }
];

export function ContentSidebar({
  onSectionSelect,
  searchQuery,
  onSearchChange,
  isCollapsed = false,
  onToggleCollapse
}: ContentSidebarProps) {
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set(['dsa', 'arrays']));
  const navigate = useNavigate();
  const { progress } = useProgress();
  const { isAdmin } = useAuth();
  const [view, setView] = useState<'tree' | 'recent' | 'bookmarks'>('tree');

  const toggleFolder = (folderId: string) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedFolders(newExpanded);
  };

  // Filter items based on search query
  const filterItems = (items: ContentItem[]): ContentItem[] => {
    return items.map(item => {
      if (item.type === 'folder') {
        const filteredChildren = item.children ? filterItems(item.children) : [];
        if (
          item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          filteredChildren.length > 0
        ) {
          return { ...item, children: filteredChildren };
        }
        return null;
      }
      
      if (item.title.toLowerCase().includes(searchQuery.toLowerCase())) {
        return item;
      }
      return null;
    }).filter((item): item is ContentItem => item !== null);
  };

  const handleItemClick = (item: ContentItem) => {
    if (item.type === 'folder') {
      toggleFolder(item.id);
    } else if (item.slug) {
      navigate(`/topic/${item.slug}`);
      onSectionSelect(item);
    }
  };

  const renderItem = (item: ContentItem, level = 0) => {
    const isExpanded = expandedFolders.has(item.id);
    const isFolder = item.type === 'folder';
    const hasChildren = isFolder && item.children && item.children.length > 0;
    const paddingLeft = level * 16 + 12;

    return (
      <motion.div
        key={item.id}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <div
          className={cn(
            "flex items-center py-2 px-3 rounded-md cursor-pointer hover:bg-gray-100 transition-colors",
            item.isPremium && "opacity-75"
          )}
          style={{ paddingLeft }}
          onClick={() => handleItemClick(item)}
        >
          <div className="flex items-center flex-1 min-w-0">
            {isFolder ? (
              <div className="flex items-center">
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4 text-gray-400 mr-2" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-400 mr-2" />
                )}
                {isExpanded ? (
                  <FolderOpen className="w-4 h-4 text-blue-500 mr-2" />
                ) : (
                  <FolderClosed className="w-4 h-4 text-blue-500 mr-2" />
                )}
              </div>
            ) : (
              <File className="w-4 h-4 text-gray-400 mr-2" />
            )}
            <span className="truncate">{item.title}</span>
          </div>
          {item.isPremium && <Lock className="w-4 h-4 text-gray-400 ml-2" />}
          {!isFolder && progress[item.id]?.completed && (
            <CheckCircle className="w-4 h-4 text-green-500 ml-2" />
          )}
        </div>
        {hasChildren && isExpanded && (
          <div className="ml-4">
            {item.children.map(child => renderItem(child, level + 1))}
          </div>
        )}
      </motion.div>
    );
  };

  return (
    <div className="h-full flex flex-col bg-white border-r">
      <div className="p-4 border-b space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search content..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setView('tree')}
            className={cn(
              "flex-1 px-3 py-1.5 rounded text-sm font-medium",
              view === 'tree' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
            )}
          >
            Tree
          </button>
          <button
            onClick={() => setView('recent')}
            className={cn(
              "flex-1 px-3 py-1.5 rounded text-sm font-medium",
              view === 'recent' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
            )}
          >
            Recent
          </button>
          <button
            onClick={() => setView('bookmarks')}
            className={cn(
              "flex-1 px-3 py-1.5 rounded text-sm font-medium",
              view === 'bookmarks' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:bg-gray-100'
            )}
          >
            Saved
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-2">
        <AnimatePresence>
          {view === 'tree' && filterItems(initialStructure).map(item => renderItem(item))}
          {view === 'recent' && (
            <div className="text-center py-8 text-gray-500">
              <p>Recent items will appear here</p>
            </div>
          )}
          {view === 'bookmarks' && (
            <div className="text-center py-8 text-gray-500">
              <p>Bookmarked items will appear here</p>
            </div>
          )}
        </AnimatePresence>
      </div>

      {isAdmin && (
        <div className="p-4 border-t">
          <button
            onClick={() => {/* Add new content logic */}}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md flex items-center justify-center space-x-2 hover:bg-blue-700"
          >
            <Plus className="w-4 h-4" />
            <span>Add Content</span>
          </button>
        </div>
      )}
    </div>
  );
}